import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateSalesChannelPayload,
  PriceListListItem,
  PromotionCampaignListItem,
  SalesApiService,
  UpdateSalesChannelPayload,
  UpsertChannelPolicyBindingPayload
} from './sales-api.service';

type ChannelTypeOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-sales-channel-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, CurrencySelectorComponent],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.channels.form.createTitle') : t('sales.channels.form.editTitle')" [description]="t('sales.channels.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/channels']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <section class="card">
          <div class="grid">
            <label>
              <span>{{ t('sales.channels.fields.code') }}</span>
              <input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('sales.channels.fields.name') }}</span>
              <input pInputText [(ngModel)]="form.name" />
            </label>
            <label>
              <span>{{ t('sales.channels.fields.channelType') }}</span>
              <p-select [options]="channelTypes" optionValue="value" [(ngModel)]="form.channelType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('sales.channels.fields.currency') }}</span>
              <ain-currency-selector [(ngModel)]="form.defaultCurrencyCode" />
            </label>
            @if (!isCreateMode()) {
              <label class="check">
                <input type="checkbox" [(ngModel)]="form.isActive" />
                <span>{{ t('sales.channels.active') }}</span>
              </label>
            }
          </div>
        </section>

        @if (!isCreateMode()) {
          <section class="card policies">
            <h3>{{ t('sales.channels.policies.title') }}</h3>
            <div class="policy-grid">
              @for (policy of policyToggles; track policy.key) {
                <div class="policy-row">
                  <label class="check">
                    <input type="checkbox" [(ngModel)]="policy.enabled" />
                    <span>{{ t('sales.channels.policies.' + policy.key) }}</span>
                  </label>
                  @if (policy.key === 'price' && policy.enabled) {
                    <label>
                      <span>{{ t('sales.channels.policies.defaultPriceList') }}</span>
                      <p-select
                        [options]="priceListOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [(ngModel)]="policy.priceListId"
                        [showClear]="true"
                        appendTo="body" />
                    </label>
                    <label class="check">
                      <input type="checkbox" [(ngModel)]="policy.requireOverrideApproval" />
                      <span>{{ t('sales.channels.policies.requireOverrideApproval') }}</span>
                    </label>
                  }
                  @if (policy.key === 'promotion' && policy.enabled) {
                    <label>
                      <span>{{ t('sales.channels.policies.defaultCampaign') }}</span>
                      <p-select
                        [options]="campaignOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [(ngModel)]="policy.campaignId"
                        [showClear]="true"
                        appendTo="body" />
                    </label>
                  }
                </div>
              }
            </div>
          </section>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .policies { margin-top: 10px; }
    .policies h3 { margin: 0 0 10px; font-size: 15px; }
    .policy-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    .policy-row { display: grid; gap: 8px; grid-column: 1 / -1; }
    @media (min-width: 900px) { .policy-row { grid-column: span 1; } }
    .message.error { color: #b91c1c; }
    @media (max-width: 760px) { .grid, .policy-grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesChannelDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly channelTypes: ChannelTypeOption[] = [
    { value: 'Pos', labelKey: 'sales.channels.types.pos' },
    { value: 'Online', labelKey: 'sales.channels.types.online' },
    { value: 'B2bPortal', labelKey: 'sales.channels.types.b2bPortal' },
    { value: 'Field', labelKey: 'sales.channels.types.field' },
    { value: 'Van', labelKey: 'sales.channels.types.van' },
    { value: 'Other', labelKey: 'sales.channels.types.other' }
  ];

  form = {
    code: '',
    name: '',
    channelType: 'Online',
    defaultCurrencyCode: 'IRR',
    isActive: true
  };

  readonly priceLists = signal<PriceListListItem[]>([]);
  readonly campaigns = signal<PromotionCampaignListItem[]>([]);

  readonly policyToggles: {
    key: string;
    enabled: boolean;
    priceListId: string | null;
    campaignId: string | null;
    requireOverrideApproval: boolean;
  }[] = [
    { key: 'price', enabled: false, priceListId: null, campaignId: null, requireOverrideApproval: false },
    { key: 'promotion', enabled: false, priceListId: null, campaignId: null, requireOverrideApproval: false },
    { key: 'credit', enabled: false, priceListId: null, campaignId: null, requireOverrideApproval: false },
    { key: 'reservation', enabled: false, priceListId: null, campaignId: null, requireOverrideApproval: false },
    { key: 'compliance', enabled: false, priceListId: null, campaignId: null, requireOverrideApproval: false }
  ];

  priceListOptions(): { label: string; value: string }[] {
    return this.priceLists().map(list => ({
      label: `${list.code} - ${list.name}`,
      value: list.id
    }));
  }

  campaignOptions(): { label: string; value: string }[] {
    return this.campaigns().map(c => ({ label: `${c.code} - ${c.name}`, value: c.id }));
  }

  ngOnInit(): void {
    this.api.listPriceLists().subscribe({
      next: lists => this.priceLists.set(lists.filter(item => item.isActive)),
      error: () => this.priceLists.set([])
    });
    this.api.listPromotionCampaigns().subscribe({
      next: lists => this.campaigns.set(lists.filter(item => item.isActive)),
      error: () => this.campaigns.set([])
    });

    const channelId = this.route.snapshot.paramMap.get('channelId');
    this.isCreateMode.set(!channelId || channelId === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    const id = channelId!;
    this.api.getChannel(id).subscribe({
      next: channel => {
        this.form = {
          code: channel.code,
          name: channel.name,
          channelType: channel.channelType,
          defaultCurrencyCode: channel.defaultCurrencyCode,
          isActive: channel.isActive
        };
        this.loadPolicyBindings(id);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.channels.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  private loadPolicyBindings(channelId: string): void {
    this.api.listChannelPolicyBindings(channelId).subscribe({
      next: bindings => {
        for (const toggle of this.policyToggles) {
          const match = bindings.find(item => item.policyKey === toggle.key);
          toggle.enabled = match?.isEnabled ?? false;
          if (toggle.key === 'price') {
            toggle.priceListId = this.readPriceListId(match?.configJson);
            toggle.requireOverrideApproval = this.readRequireOverrideApproval(match?.configJson);
          }
          if (toggle.key === 'promotion') {
            toggle.campaignId = this.readCampaignId(match?.configJson);
          }
        }
      },
      error: () => {}
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      const payload: CreateSalesChannelPayload = {
        code: this.form.code,
        name: this.form.name,
        channelType: this.form.channelType,
        defaultCurrencyCode: this.form.defaultCurrencyCode
      };
      this.api.createChannel(payload).subscribe({
        next: channel => void this.router.navigate(['/sales/channels', channel.id]),
        error: () => {
          this.errorMessage.set(this.t('sales.channels.errorSave'));
          this.saving.set(false);
        }
      });
      return;
    }

    const channelId = this.route.snapshot.paramMap.get('channelId')!;
    const payload: UpdateSalesChannelPayload = {
      name: this.form.name,
      channelType: this.form.channelType,
      defaultCurrencyCode: this.form.defaultCurrencyCode,
      isActive: this.form.isActive
    };
    this.api.updateChannel(channelId, payload).subscribe({
      next: () => {
        const bindings: UpsertChannelPolicyBindingPayload[] = this.policyToggles.map(toggle => ({
          policyKey: toggle.key,
          isEnabled: toggle.enabled,
          configJson:
            toggle.key === 'price' && toggle.enabled
              ? JSON.stringify({
                  priceListId: toggle.priceListId ?? undefined,
                  requireOverrideApproval: toggle.requireOverrideApproval
                })
              : toggle.key === 'promotion' && toggle.enabled && toggle.campaignId
                ? JSON.stringify({ campaignId: toggle.campaignId })
                : undefined
        }));
        this.api.replaceChannelPolicyBindings(channelId, bindings).subscribe({
          next: () => {
            this.saving.set(false);
            void this.router.navigate(['/sales/channels']);
          },
          error: () => this.failSave()
        });
      },
      error: () => this.failSave()
    });
  }

  private readPriceListId(configJson?: string): string | null {
    if (!configJson) {
      return null;
    }
    try {
      const parsed = JSON.parse(configJson) as { priceListId?: string };
      return parsed.priceListId ?? null;
    } catch {
      return null;
    }
  }

  private readRequireOverrideApproval(configJson?: string): boolean {
    if (!configJson) {
      return false;
    }
    try {
      const parsed = JSON.parse(configJson) as { requireOverrideApproval?: boolean };
      return parsed.requireOverrideApproval === true;
    } catch {
      return false;
    }
  }

  private readCampaignId(configJson?: string): string | null {
    if (!configJson) return null;
    try {
      const parsed = JSON.parse(configJson) as { campaignId?: string };
      return parsed.campaignId ?? null;
    } catch {
      return null;
    }
  }

  private failSave(): void {
    this.errorMessage.set(this.t('sales.channels.errorSave'));
    this.saving.set(false);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
